import type { Edge, ReactFlowInstance, XYPosition } from '@xyflow/react'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import {
  calculateNodeHeight,
  NODE_WIDTH,
} from '@/components/pages/Digraph/VariableWithBarChart/VariableWithBarChart'
import { Network } from '@/hooks/network/useNetwork'
import { LayoutMethod } from '@/services/models/enums'
import { VariableId, VariableStateIndex } from '@/typeAlias'
import { Variable } from '@/types/variable'
import { showError } from '@/utils/toast'

export { LayoutMethod }

export const MIN_NODE_WIDTH = 120
export const MAX_NODE_WIDTH = 360
const NODE_WIDTH_STORAGE_KEY = 'digraph.nodeWidth'

const clampNodeWidth = (width: number): number =>
  Math.min(MAX_NODE_WIDTH, Math.max(MIN_NODE_WIDTH, Math.round(width)))

const readStoredNodeWidth = (): number => {
  if (typeof window === 'undefined') return NODE_WIDTH
  const raw = window.localStorage.getItem(NODE_WIDTH_STORAGE_KEY)
  const parsed = raw ? Number(raw) : NaN
  return Number.isFinite(parsed) ? clampNodeWidth(parsed) : NODE_WIDTH
}

type GlobalState = {
  layoutMethod: LayoutMethod
  // User-adjustable width applied to every digraph node. Persisted to
  // localStorage; read by the node component and the edge/center geometry
  // (via useGlobalState.getState().nodeWidth) so they stay in sync.
  nodeWidth: number
  isCreatingLayout: boolean
  // Ephemeral digraph "show the whole network" overview toggle. When on,
  // the view selector is disabled and every node is laid out via an
  // auto-layout that is never persisted to a view. The graph is fully
  // interactive (context menus, editing, dragging, arrow drawing), but it is
  // read-only *toward the view*: node positions, membership, layout, and the
  // camera are never saved, and view-only menu actions are hidden. Reset on
  // network/view change and on full reload (module re-init).
  isShowingAll: boolean
  shownVariables: ShownVariables
  // Which view `shownVariables` was loaded from, or undefined when they came
  // from somewhere else (the "All" overview) or were cleared. Set atomically
  // with `shownVariables` so a reader can tell whether the nodes on the canvas
  // are the current view's -- a view switch leaves the previous view's nodes
  // up until the new ones arrive (KBE-915).
  shownVariablesViewId: string | undefined
  contextMenuPosition?: XYPosition
  edge?: Edge
  variableState?: SelectedVariableState
  cptCell?: CptCellContext
  nonModalDialogs: NonModalDialog[] // ordered by what their z-index should be
  assumptions: Map<VariableId, VariableStateIndex>
  variableIsBeingDragged?: boolean // needed as if the mouse movement during a drag event stops while the mouse left button is still triggered, the `variableLastDragged` won't updated, the view would be queried causing a flickering
  variableLastDragged?: Date // needed to avoid flickering when there's an active query on the view while dragging
}

export type ShownVariables = Map<string, { center: XYPosition }>

export type SelectedVariableState = {
  variableId: string
  stateId: string
  isOptimalState?: boolean
  isFaultState?: boolean
}

export type CptCellContext = {
  variableId: string
  columnIndex: number
  rowState: number
}

type Arrow = { sourceId: string; targetId: string }

type NonModalDialog = {
  initialPlacement: number
} & (
  | {
      type: 'EditVariable'
      variableId: string
    }
  | { type: 'EditArrow'; arrow: Arrow }
)

// `stateIndex` is the hard observed state; it is absent for an assumption that
// carries only a measurement. `intervalValue` is the researcher's measurement
// when they entered one, and it is carried RAW: KBE-840 moved the evidence funnel
// server-side, so nothing here resolves it into a probability vector any more.
// The engine uses the measurement in place of any state index when it can read
// it against the variable's scale (gotcha 35).
export type Assumption = {
  variableId: string
  stateIndex?: number
  intervalValue?: number | string | null
}

type Actions = {
  changeLayoutMethod: (
    layoutMethod: LayoutMethod,
    network: Network,
    reactFlow: ReactFlowInstance,
    nodePositions: Map<string, XYPosition>,
  ) => void
  showVariables: (
    nodes: { center: XYPosition; variable: Variable }[],
    reactFlow: ReactFlowInstance,
  ) => void
  showAllVariables: (
    network: Network,
    reactFlow: ReactFlowInstance,
    nodePositions: Map<string, XYPosition>,
  ) => void
  hideVariables: (predicate: (id: string) => boolean) => void
  clearVariableState: () => void
  applyVariablePositionChanges: (changes: Map<string, XYPosition>) => void
  openContextMenu: (
    position: XYPosition,
    selected?: {
      edge?: Edge
      variableState?: SelectedVariableState
      cptCell?: CptCellContext
    },
  ) => void
  closeContextMenu: () => void
  handleNetworkChange: () => void
  handleViewChange: () => void
  toggleAssumption: (variableId: string, newStateIndex: number) => void
  clearAssumption: (variableIds: string[]) => void
  startEditingVariables: (ids: string[]) => void
  stopEditingVariables: (ids: string[]) => void
  bringEditVariableDialogToTheFront: (id: string) => void
  startEditingArrow: (arrow: Arrow) => void
  stopEditingArrows: (arrows: Arrow[]) => void
  bringEditArrowDialogToTheFront: (arrow: Arrow) => void
  handleVariableDeletion: (predicate: (id: string) => boolean) => void
  handleViewUpdate: (
    viewVariables: ShownVariables,
    layout: LayoutMethod,
    viewId: string,
    options?: { force?: boolean },
  ) => void
  handleVariableDragStatusChange: (isBeingDragged: boolean) => void
  setNodeWidth: (width: number) => void
  focusVariables: (
    ids: Set<string>,
    network: Network,
    reactFlow: ReactFlowInstance,
  ) => void
  setLayoutCreatingProgress: (inProgress: boolean) => void
  setShowingAll: (isShowingAll: boolean) => void
}

const nonModalDialogLimit = 5

/**
 * The chosen technology for global state is Zustand, since:
 * - It allows encapsulating the state management logic in a single file.
 * - It doesn't require a lot of boilerplate.
 * - It can be used outside of React too, which could become useful to us at some point.
 */
export const useGlobalState = create(
  // We use Immer to ensure that global state is immutable, but it can be easily modified from actions.
  immer<GlobalState & Actions>((set, get) => ({
    layoutMethod: LayoutMethod.LAYERED,
    nodeWidth: readStoredNodeWidth(),
    isCreatingLayout: false,
    isShowingAll: false,
    shownVariables: new Map(),
    shownVariablesViewId: undefined,
    nonModalDialogs: [],
    assumptions: new Map(),
    setNodeWidth(width) {
      const clamped = clampNodeWidth(width)
      set((state) => {
        state.nodeWidth = clamped
      })
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(NODE_WIDTH_STORAGE_KEY, String(clamped))
      }
    },
    changeLayoutMethod(layoutMethod, network, reactFlow, nodePositions) {
      set((state) => {
        state.layoutMethod = layoutMethod
        state.shownVariables = createVariablesFromPositions(nodePositions)
      })
      fitBoundsToVariables(new Set(nodePositions.keys()), network, reactFlow)
    },
    toggleAssumption(variableId, newStateIndex) {
      set((state) => {
        // stateIndex === -1 indicates an error from the Bayesian API
        const currentAssumption = state.assumptions.get(variableId)
        const isSameAssumption = currentAssumption === newStateIndex

        if (isSameAssumption) {
          // Toggle off - remove the assumption
          state.assumptions.delete(variableId)
        } else if (newStateIndex > -1) {
          // Set or update the assumption
          state.assumptions.set(variableId, newStateIndex)
        } else {
          // newStateIndex === -1 (error case) - remove the assumption
          state.assumptions.delete(variableId)
        }
      })
    },
    clearAssumption(variableIds) {
      set((state) => {
        for (const variableId of variableIds) {
          state.assumptions.delete(variableId)
        }
      })
    },
    startEditingVariables(variableIds) {
      set((state) => {
        const idsOfAlreadyEditedVariables = state.nonModalDialogs.flatMap(
          (d) => (d.type === 'EditVariable' ? [d.variableId] : []),
        )
        const newIds = variableIds.filter(
          (id) => !idsOfAlreadyEditedVariables.includes(id),
        )
        if (newIds.length === 0) {
          if (variableIds.length > 1) {
            showError('You are already editing these variables.')
            return
          } else {
            showError('You are already editing this variable.')
            return
          }
        }
        if (state.nonModalDialogs.length + newIds.length > 5) {
          showErrorOpeningTooManyNonModalDialogs()
          return
        }
        const takenPlacements = state.nonModalDialogs.map(
          (d) => d.initialPlacement,
        )
        const freePlacements = getFreeNonModalDialogPlacements(takenPlacements)
        const newDialogs = newIds.map((id, i) => ({
          initialPlacement: freePlacements[i],
          type: 'EditVariable' as const,
          variableId: id,
        }))
        state.nonModalDialogs = [...state.nonModalDialogs, ...newDialogs]
      })
    },
    stopEditingVariables(variableIds) {
      set((state) => {
        state.nonModalDialogs = state.nonModalDialogs.filter(
          (d) =>
            d.type !== 'EditVariable' || !variableIds.includes(d.variableId),
        )
      })
    },
    bringEditVariableDialogToTheFront(variableId) {
      set((state) => {
        const currentIndex = state.nonModalDialogs.findIndex(
          (d) => d.type === 'EditVariable' && d.variableId === variableId,
        )
        const [d] = state.nonModalDialogs.splice(currentIndex, 1)
        state.nonModalDialogs.push(d)
      })
    },
    startEditingArrow(arrow) {
      set((state) => {
        const arrowIsAlreadyBeingEdited = state.nonModalDialogs.some(
          (d) =>
            d.type === 'EditArrow' &&
            d.arrow.sourceId === arrow.sourceId &&
            d.arrow.targetId === arrow.targetId,
        )
        if (arrowIsAlreadyBeingEdited) {
          showError('You are already editing this arrow.')
          return
        }
        if (state.nonModalDialogs.length >= nonModalDialogLimit) {
          showErrorOpeningTooManyNonModalDialogs()
          return
        }
        const takenPlacements = state.nonModalDialogs.map(
          (d) => d.initialPlacement,
        )
        const freePlacements = getFreeNonModalDialogPlacements(takenPlacements)
        state.nonModalDialogs = [
          ...state.nonModalDialogs,
          { initialPlacement: freePlacements[0], type: 'EditArrow', arrow },
        ]
      })
    },
    stopEditingArrows(arrows) {
      set((state) => {
        const sourceIds = arrows.map((a) => a.sourceId)
        const targetIds = arrows.map((a) => a.targetId)
        state.nonModalDialogs = state.nonModalDialogs.filter(
          (d) =>
            !(
              d.type === 'EditArrow' &&
              sourceIds.includes(d.arrow.sourceId) &&
              targetIds.includes(d.arrow.targetId)
            ),
        )
      })
    },
    bringEditArrowDialogToTheFront(arrow) {
      set((state) => {
        const currentIndex = state.nonModalDialogs.findIndex(
          (d) =>
            d.type === 'EditArrow' &&
            d.arrow.sourceId === arrow.sourceId &&
            d.arrow.targetId === arrow.targetId,
        )
        const [d] = state.nonModalDialogs.splice(currentIndex, 1)
        state.nonModalDialogs.push(d)
      })
    },
    showVariables(
      nodes: { center: XYPosition; variable: Variable }[],
      reactFlow: ReactFlowInstance,
    ) {
      set((state) => {
        const newShownVariables = new Map(
          nodes.map((node) => [node.variable.id, { center: node.center }]),
        )
        state.shownVariables = new Map([
          ...state.shownVariables,
          ...newShownVariables,
        ])
      })
      fitViewportToNodes(nodes, reactFlow)
    },
    showAllVariables(network, reactFlow, nodePositions) {
      set((state) => {
        // Deliberately leaves shownVariablesViewId alone. This serves two
        // callers: the ephemeral "All" overview, whose entry through
        // setShowingAll has already cleared it, and the AppHeader "Show all"
        // button, which persists every node INTO the open view -- there the
        // nodes are still that view's, and clearing the id would switch off
        // its camera persistence for the rest of the session (KBE-915).
        state.shownVariables = createVariablesFromPositions(nodePositions)
      })
      fitBoundsToVariables(network.variableIds, network, reactFlow)
    },
    hideVariables(predicate) {
      set((state) => {
        for (const id of state.shownVariables.keys()) {
          if (predicate(id)) {
            state.shownVariables.delete(id)
            state.assumptions.delete(id)
          }
        }
      })
    },
    clearVariableState() {
      set((state) => {
        state.variableState = undefined
      })
    },
    applyVariablePositionChanges(changes) {
      set((state) => {
        for (const [id, position] of changes) {
          const variable = state.shownVariables.get(id)
          // Ignore stale drag updates for nodes no longer shown (e.g. a
          // position change arriving after the All toggle cleared
          // shownVariables) instead of crashing the drag handler.
          if (!variable) continue
          variable.center = position
        }
      })
    },
    openContextMenu(position, selected) {
      set((state) => {
        state.contextMenuPosition = position
        state.edge = selected?.edge
        state.variableState = selected?.variableState
        state.cptCell = selected?.cptCell
      })
    },
    closeContextMenu() {
      set((state) => {
        state.contextMenuPosition = undefined
      })
    },
    handleNetworkChange() {
      set((state) => {
        state.shownVariables = new Map()
        state.shownVariablesViewId = undefined
        state.layoutMethod = LayoutMethod.LAYERED
        state.nonModalDialogs = []
        state.assumptions = new Map()
        state.isShowingAll = false
      })
    },
    handleViewChange() {
      set((state) => {
        state.shownVariables = new Map()
        state.shownVariablesViewId = undefined
        state.nonModalDialogs = []
        state.assumptions = new Map()
        state.isShowingAll = false
      })
    },
    handleVariableDeletion(predicate) {
      get().hideVariables(predicate)
      const ids = get().nonModalDialogs.flatMap((d) =>
        d.type === 'EditVariable' && predicate(d.variableId)
          ? [d.variableId]
          : [],
      )
      get().stopEditingVariables(ids)
      set((state) => {
        state.nonModalDialogs = state.nonModalDialogs.filter(
          (d) =>
            !(
              d.type === 'EditArrow' &&
              (predicate(d.arrow.sourceId) || predicate(d.arrow.targetId))
            ),
        )
      })
    },
    handleViewUpdate(newShownVariables, layoutMethod, viewId, options) {
      set((state) => {
        const refetchInterval = process.env.NEXT_PUBLIC_REFETCH_INTERVAL
        if (!refetchInterval)
          throw new Error('NEXT_PUBLIC_REFETCH_INTERVAL should be defined')

        const lastDragElapsedMilliseconds = state.variableLastDragged
          ? new Date().getTime() - state?.variableLastDragged?.getTime()
          : undefined

        const draggedWithinRefetchInterval =
          lastDragElapsedMilliseconds &&
          lastDragElapsedMilliseconds < parseInt(refetchInterval)
        // `force` is the initial application of a view to a page instance (a
        // fresh mount or a view switch). The drag-skip guards a *background*
        // refresh of the view already on screen from clobbering an in-progress
        // drag; it must never suppress the initial load, which is what left a
        // returned-to digraph blank until the view was re-selected (KBE-874).
        const skipViewUpdate =
          !options?.force &&
          (state.variableIsBeingDragged || draggedWithinRefetchInterval)

        if (!skipViewUpdate) {
          state.shownVariables = newShownVariables
          state.shownVariablesViewId = viewId
        }
      })
      if (layoutMethod !== get().layoutMethod) {
        set((state) => {
          state.layoutMethod = layoutMethod
        })
      }
    },
    handleVariableDragStatusChange(isBeingDragged: boolean) {
      set((state) => {
        state.variableIsBeingDragged = isBeingDragged
        if (isBeingDragged) {
          state.variableLastDragged = new Date()
        }
      })
    },
    focusVariables(ids, network, reactFlow) {
      fitBoundsToVariables(ids, network, reactFlow)
    },
    setLayoutCreatingProgress(inProgress) {
      set((state) => {
        state.isCreatingLayout = inProgress
      })
    },
    setShowingAll(isShowingAll) {
      set((state) => {
        // No-op when the value is unchanged so idempotent callers (unmount
        // cleanup, Strict Mode remount, a network-change effect while All is
        // already off) can't wipe a loaded view.
        if (state.isShowingAll === isShowingAll) return
        state.isShowingAll = isShowingAll
        // Drop the transient render state on a real toggle. On enter, the
        // overview relays out (useShowAllOverview); on exit, useView reloads
        // the selected view. Clearing the stale all-node layout here closes
        // the window during which a drag could persist overview positions into
        // the real view before useView's effect lands.
        state.shownVariables = new Map()
        state.shownVariablesViewId = undefined
        state.assumptions = new Map()
        state.nonModalDialogs = []
        // Close any open context menu so it can't remount stale (in its old
        // place) after the toggle.
        state.contextMenuPosition = undefined
        // Clear drag bookkeeping too: otherwise a quick toggle right after a
        // drag makes the post-toggle handleViewUpdate no-op (it skips while a
        // recent drag is within the refetch interval), leaving an empty graph.
        state.variableIsBeingDragged = false
        state.variableLastDragged = undefined
      })
    },
  })),
)

export const areAllShown = (variableIds: Set<string>) => {
  const shownVariables = useGlobalState.getState().shownVariables
  return [...variableIds].every((id) => shownVariables.has(id))
}

/**
 * @deprecated Use {@link fitViewportToNodes} instead. That one doesn't throw errors.
 */
const fitBoundsToVariables = (
  variableIds: Set<string>,
  network: Network,
  reactFlow: ReactFlowInstance,
) => {
  const shownVariables = useGlobalState.getState().shownVariables
  const nodes = [...variableIds].map((id) => {
    const v = network.variableById.get(id)
    const position = shownVariables.get(id)?.center
    if (!v)
      throw new Error(
        'Variable not found in network! (useGlobalState/fitBoundsToVariables)',
      )
    if (!position)
      throw new Error(
        'Variable position not found! (useGlobalState/fitBoundsToVariables)',
      )
    return { center: position, variable: v }
  })
  fitViewportToNodes(nodes, reactFlow)
}

function fitViewportToNodes(
  nodes: { center: XYPosition; variable: Variable }[],
  reactFlow: ReactFlowInstance,
) {
  if (nodes.length === 0) {
    return
  }
  const nodeWidth = useGlobalState.getState().nodeWidth
  const variables = nodes.map((node) => {
    const position = node.center
    const height = calculateNodeHeight(node.variable.stateIds.length)
    return {
      left: position.x - nodeWidth / 2,
      right: position.x + nodeWidth / 2,
      top: position.y - height / 2,
      bottom: position.y + height / 2,
    }
  })
  const left = Math.min(...variables.map((v) => v.left))
  const right = Math.max(...variables.map((v) => v.right))
  const top = Math.min(...variables.map((v) => v.top))
  const bottom = Math.max(...variables.map((v) => v.bottom))
  void reactFlow.fitBounds(
    {
      x: left,
      y: top,
      width: right - left,
      height: bottom - top,
    },
    { padding: 0.1 },
  )
}

function getFreeNonModalDialogPlacements(takenPlacements: number[]) {
  const allIndices = [0, 1, 2, 3, 4]
  return allIndices.filter((idx) => !takenPlacements.includes(idx))
}

function showErrorOpeningTooManyNonModalDialogs() {
  showError('Maximum five non-modal dialogs can be open at the same time.')
}

function createVariablesFromPositions(nodePositions: Map<string, XYPosition>) {
  return new Map(
    [...nodePositions].map(([id, position]) => [
      id,
      {
        center: position,
      },
    ]),
  )
}
